import { isNativeApp, getPlatform } from './platform';

const FIRST_OPEN_KEY = 'snugtale_first_open_v1';

/**
 * Anonymous install proxy: ping the backend exactly once per install on
 * native app boot. No user id, no device id — the server stores only
 * (event, app_id, platform, timestamp), so this stays out of the Play
 * Data Safety declarations. The localStorage flag is set BEFORE the
 * request so a failed send can never turn into a double count.
 */
export function pingFirstOpen(): void {
  try {
    if (!isNativeApp()) return;
    if (localStorage.getItem(FIRST_OPEN_KEY)) return;
    localStorage.setItem(FIRST_OPEN_KEY, String(Date.now()));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'first_open',
        app_id: 'snugtale',
        platform: getPlatform(),
      }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* storage unavailable — skip silently */ }
}

/**
 * Native purchase diagnostics. Sent when a RevenueCat/StoreKit/Play purchase
 * fails (or offerings can't be loaded) so the exact error is readable
 * server-side (funnel_events.metadata) instead of needing a debug build on
 * the tester's phone. Bounded free text, no identifiers.
 */
export function reportPurchaseError(fields: Record<string, string | number | boolean>): void {
  try {
    if (!isNativeApp()) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'purchase_error',
        app_id: 'snugtale',
        platform: getPlatform(),
        metadata: fields,
      }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

/**
 * Native sign-in diagnostics (Google / Apple plugins). The plugins fail on the
 * device — Credential Manager, console setup, plugin options — without any
 * request reaching the backend, so the error message is otherwise invisible.
 * Bounded free text, no identifiers, no tokens.
 */
export function reportAuthError(fields: Record<string, string | number | boolean>): void {
  try {
    if (!isNativeApp()) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'auth_error',
        app_id: 'snugtale',
        platform: getPlatform(),
        metadata: fields,
      }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

/**
 * The reader's closing slide (native, logged-out) was tapped → register.
 * Anonymous count only, so the funnel can read taps against registrations.
 */
export function reportReaderSignupTap(): void {
  try {
    if (!isNativeApp()) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'reader_signup_tap', app_id: 'snugtale', platform: getPlatform() }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

/**
 * Store-review prompt telemetry: the app asked the OS for a review dialog
 * (see lib/reviewPrompt.ts). Only the trigger name travels — no identifiers —
 * so prompt volume can be read against rating growth in the funnel report.
 * Neither store tells us whether the dialog was actually shown.
 */
export function reportReviewPrompt(trigger: string): void {
  try {
    if (!isNativeApp()) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'review_prompt_requested',
        app_id: 'snugtale',
        platform: getPlatform(),
        metadata: { trigger },
      }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

/**
 * Sample-story landings (2026-09-07): the ad arm that lands straight in a shared
 * story pings the same `landing_view` the static /start/ template sends, with
 * t = 'ad-<key>' and h = 'sample', so the daily report's landing line carries
 * both arms. Web-side, no ids.
 */
export function reportLandingView(t: string, h: string): void {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'landing_view', app_id: 'snugtale', platform: getPlatform(), metadata: { t, h } }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

/**
 * Engagement beacon of a sample-story landing (2026-09-07): the shared reader
 * sends `landing_engage {t, h, what}` for the ad arm — page3 / last / slide
 * (reached the closing slide) and dwell15 / dwell60 — once per kind per view,
 * mirroring what public/start/start.js sends for the static landing. No ids.
 * Platform = where it was read (2026-10-07): the app's reader sends the same beacons, and until then they were all
 * stored as 'web', so app reading counted as web reading.
 */
export function reportLandingEngage(t: string, h: string, what: string): void {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'landing_engage', app_id: 'snugtale', platform: getPlatform(), metadata: { t, h, what } }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

/**
 * Web → app bridge: one ping per view of the `/app?src=…` redirect route
 * (docs/WEB-TO-APP-BRIDGE-2026-09.md). Metadata is the source slug and the
 * detected store only — no ids — so installs (Play/ASC campaign reports) can be
 * read against taps per moment. Unlike the helpers above this one is WEB-side:
 * no native guard, and `keepalive` so the store redirect that follows ~600 ms
 * later cannot cancel it.
 */
/** A tap on a "keep this trial story" control — strip (above the book), banner (below), banner_login. */
export type ClaimPlacement = 'strip' | 'banner' | 'banner_login' | 'strip_email' | 'banner_email' | 'dialog_register' | 'email_link'
  | 'sheet_app' | 'sheet_register' | 'sheet_login' | 'sheet_email' | 'banner_app' | 'slide_app' | 'slide_register' | 'slide_email' | 'code_home' | 'code_create' | 'register_iab_app'
  // 2026-10-02: the "Morgen wieder? ja/vielleicht" step, the later-day welcome-back card, a trial holder's sample slide.
  | 'slide_again' | 'reopen_card' | 'slide_holder'
  // 2026-10-15: the Folge 3 teaser's „Kostenlos öffnen“ on the own trial's closing slide (lib/trialTeaser.ts).
  | 'slide_next'
  // 2026-10-07, inside the account sheet: shown, a provider button, closed without any choice (the „ja“ analysis
  // could not see what P4 did in the sheet).
  | 'sheet_open' | 'sheet_google' | 'sheet_apple' | 'sheet_facebook' | 'sheet_close'
  // 2026-10-20: „Andere Möglichkeiten“ unfolded inside the sheet (not a choice).
  | 'sheet_more';
/** `extra` (2026-10-20, the sheet's events): `from` = teaser | keep (what opened the sheet), on sheet_close also `via` =
 *  x | backdrop | esc | back | leave and `secs` (how long it was open). No ids. */
export function reportClaimTap(placement: ClaimPlacement, extra: Record<string, string | number> = {}): void {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'anon_claim_tap', app_id: 'snugtale', platform: getPlatform(), metadata: { placement, ...extra } }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

export function reportAppBridgeTap(src: string, target: string): void {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'app_bridge_tap',
        app_id: 'snugtale',
        platform: getPlatform(),
        metadata: { src, target },
      }),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* ignore */ }
}

function pingAnon(event: string, metadata: Record<string, string | number>): void {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST', credentials: 'omit', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, app_id: 'snugtale', platform: getPlatform(), metadata }),
    }).catch(() => undefined);
  } catch { /* never block the page */ }
}

/** A trial story's link opened again on a later calendar day (2026-09-26): does anyone come back to a free story?
 *  `owner` = this device made it. Once per story per day (the caller dedupes). No ids. */
export function reportTrialReopened(days: number, owner: boolean): void {
  pingAnon('anon_story_reopened', { days, owner: owner ? 'yes' : 'no' });
}

/** How /purchase was reached (2026-10-02): the Home pages badge, Settings, the create wizard's out-of-pages box,
 *  a 402 on create, or anything else (a direct/deep link, a reload, the sign-in bounce). */
export type PaywallSrc = 'badge' | 'settings' | 'create' | 'create_402' | 'deeplink';
export const PAYWALL_SRCS: readonly PaywallSrc[] = ['badge', 'settings', 'create', 'create_402', 'deeplink'];

/** The out-of-pages screen (create) or /purchase was shown (2026-09-26) — the paywall was invisible before.
 *  2026-10-02: `acct` = free | paid (user.is_free: the store reviewer's demo account and the testers are free — all
 *  three "no-account" views of 09-27…10-01 were the reviewer) and, for /purchase, `src`. Neither is an id. */
export function reportPaywallView(where: 'create' | 'purchase', acct: 'free' | 'paid', src?: PaywallSrc): void {
  pingAnon('paywall_view', src ? { where, acct, src } : { where, acct });
}

/** What a fresh native install tapped first on the signed-out library (2026-10-02; none = no tap within 60 s).
 *  Sent once per install by lib/nativeFirstTap.ts. No ids. */
export type NativeFirstTap = 'invite' | 'start_free' | 'sign_in' | 'story' | 'import' | 'none';
export function reportNativeFirstTap(what: NativeFirstTap): void {
  pingAnon('native_first_tap', { what });
}

/** Story-first native first run (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md): one ping per step reached or exit
 *  taken. welcome | who | loves = the step was shown; pick = a welcome theme tile was tapped (2026-10-11; German: its
 *  submit follows at once, `instant` = the pooled story; other languages: the idea step); submit = the create tap; created = the story exists (trial POST
 *  201, or the account story after sign-up); failed = the POST failed (caps, provider, network); signup = the
 *  under-13 variant handed over to the sign-up page; library | login | import = the welcome screen's other doors.
 *  `variant` trial = story without an account (iOS, Android 13+), account = Android under 13 after the parent gate.
 *  `named` / `instant` (pooled story) as 1 | 0 where known. Keeping the story is read server-side (origin first_run
 *  on anon_story_created → stories.claimed_at). No ids. */
export type FirstRunStep = 'welcome' | 'pick' | 'who' | 'loves' | 'submit' | 'created' | 'failed' | 'signup' | 'library' | 'login' | 'import_open' | 'import';
export type FirstRunVariant = 'trial' | 'account';
export const FIRST_RUN_STEPS: readonly FirstRunStep[] = ['welcome', 'pick', 'who', 'loves', 'submit', 'created', 'failed', 'signup', 'library', 'login', 'import_open', 'import'];
export function reportFirstRun(step: FirstRunStep, variant: FirstRunVariant, extra: { named?: boolean; instant?: boolean } = {}): void {
  const metadata: Record<string, string | number> = { step, variant };
  if (extra.named !== undefined) metadata.named = extra.named ? 1 : 0;
  if (extra.instant !== undefined) metadata.instant = extra.instant ? 1 : 0;
  pingAnon('first_run', metadata);
}

/** Android's neutral age screen was answered (2026-10-02): only the band, never the age. */
export function reportAgeGateAnswered(band: 'under13' | '13plus'): void {
  pingAnon('age_gate_answered', { band });
}

/** A trial code typed into the app's import card opened the story in the app (2026-09-26; `welcome` = the native first
 *  run's welcome screen, 2026-10-02). `paste` (2026-10-20): yes = the one-tap „Einfügen“, no = typed or long-pressed. */
export function reportTrialImported(via: 'library' | 'home' | 'create' | 'welcome', pasted = false): void {
  pingAnon('trial_imported', { via, paste: pasted ? 'yes' : 'no' });
}

/** After /app sent a phone to its store: did the page go hidden (another app came up), unload (the store page
 *  loaded inside this browser) or stay (nothing happened)? A heuristic for whether the Facebook/Instagram
 *  browsers hand a store link over at all (2026-09-26). `copied` (2026-10-07): the trial's code was on the clipboard
 *  when the store opened — none = a door without a trial code. `acct` (2026-10-23): a signed-in web parent, who was
 *  shown which sign-in to use in the app. */
export function reportBridgeHandoff(src: string, target: string, browser: string, hidden: 'yes' | 'unload' | 'no',
  copied: 'yes' | 'no' | 'none', acct: 'yes' | 'no' = 'no'): void {
  pingAnon('app_bridge_handoff', { src, target, browser, hidden, copied, acct });
}

/** One ping per auth page view from a social in-app browser (2026-09-17): how many Meta visitors reach
 * /register or /login inside Facebook/Instagram, where Google sign-in is impossible. */
export function reportRegisterView(browser: 'facebook' | 'instagram' | 'messenger', page: 'register' | 'login'): void {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/event`, {
      method: 'POST', credentials: 'omit', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'register_view', app_id: 'snugtale', platform: getPlatform(), metadata: { browser, page } }),
    }).catch(() => undefined);
  } catch { /* never block the page */ }
}
